import React from "react";
import { AlertTriangle, CheckCircle2, Trash2, MapPin, MinusCircle } from "lucide-react";
import { evaluateMarker, STATUS_COLORS } from "@/lib/rooms";

const STATUS_LABEL = { ideal: "Ideal", ok: "Acceptable", conflict: "Conflict", unknown: "Draw polygon" };

export default function RoomMarkerInspector({ marker, center, rotation, onDelete }) {
  const ev = evaluateMarker(marker, center, rotation);
  const color = STATUS_COLORS[ev.status];
  const Icon = ev.status === "conflict" ? AlertTriangle : ev.status === "unknown" ? MinusCircle : CheckCircle2;

  return (
    <div className="p-5 flex flex-col gap-4" data-testid="marker-inspector">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1.5">
          <MapPin className="h-3 w-3" /> Room marker
        </div>
        <div className="font-display text-3xl font-bold text-slate-100">{ev.room.label}</div>
      </div>

      <div className="flex items-center gap-2 p-3 rounded-lg border" style={{ borderColor: `${color}66`, background: `${color}14` }} data-testid="marker-inspector-status">
        <Icon className="h-5 w-5 shrink-0" style={{ color }} />
        <div className="text-sm text-slate-100 leading-snug">
          {ev.direction ? (
            <>
              <span className="font-semibold">{ev.room.label}</span> in <span className="font-mono font-semibold" style={{ color: ev.info.color }}>{ev.direction}</span> ({ev.info.sanskrit})
              {" — "}<span style={{ color }} className="font-semibold">{STATUS_LABEL[ev.status]}</span>
            </>
          ) : "Draw the house polygon to evaluate this room's direction."}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Ideal</div>
          <div className="font-mono text-sm text-emerald-400">{ev.room.ideal.join(", ")}</div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Acceptable</div>
          <div className="font-mono text-sm text-amber-400">{ev.room.ok.join(", ") || "—"}</div>
        </div>
      </div>

      {ev.status === "conflict" && (
        <div className="p-4 rounded-lg border border-red-500/30 bg-red-950/30" data-testid="marker-inspector-remedy">
          <div className="font-mono text-[10px] uppercase tracking-widest text-red-300 mb-2">Remedy</div>
          <div className="text-sm text-slate-200 leading-relaxed">{ev.room.remedy}</div>
        </div>
      )}

      <button
        type="button"
        data-testid="delete-marker-button"
        onClick={() => onDelete(marker.id)}
        className="flex items-center gap-2 text-sm text-slate-400 hover:text-red-300 transition-colors self-start"
      >
        <Trash2 className="h-4 w-4" /> Remove marker
      </button>
    </div>
  );
}
